// Fetch GenCC validity for one gene symbol (TGCLOUD-1794, Phase 2).
//
// Read-only and side-effect free with respect to the genotype record: this
// never touches the modal's form state or its dirty flag.
//
// The response is passed through UNMODIFIED, including `available: false`.
// Flattening a failed lookup to an empty list here would erase the difference
// between "GenCC has no submission for this gene" and "we could not ask", and
// the second rendered as the first is a clinical claim made on the strength of
// a network timeout — the failure the gene→disease lookup was burned by
// (TGCLOUD-1785).

import { useEffect, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

export default function useGeneValidity(geneSymbol, { enabled = true } = {}) {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  // Guards against a late response for a gene the user has already navigated
  // away from overwriting the current one.
  const requestedFor = useRef(null);

  useEffect(() => {
    const symbol = typeof geneSymbol === 'string' ? geneSymbol.trim() : '';
    if (!enabled || !symbol) {
      setResponse(null);
      setLoading(false);
      requestedFor.current = null;
      return undefined;
    }

    let cancelled = false;
    requestedFor.current = symbol;
    setLoading(true);

    (async () => {
      try {
        const url = APICONSTANTS.genccByGene.replace(
          ':geneSymbol',
          encodeURIComponent(symbol)
        );
        const { data } = await axios.get(url);
        if (cancelled || requestedFor.current !== symbol) return;
        setResponse(data ?? { available: false });
      } catch (err) {
        if (cancelled || requestedFor.current !== symbol) return;
        // A thrown request is an unavailable answer, never an empty one.
        setResponse({
          available: false,
          reason: 'unreachable',
          assertions: []
        });
      } finally {
        if (!cancelled && requestedFor.current === symbol) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [geneSymbol, enabled]);

  return { response, loading };
}
